// Shared site chrome for every /site page: header, full-screen menu overlay, tab bar (below 640px), footer.
// Usage: import { mountChrome } from "../shared/chrome"; mountChrome({ active: "home" });
// Renders into <div id="chrome-header"></div> and <div id="chrome-footer"></div>.
// The header slot reserves its own height in the flow, so pages need no top padding.
// Motion: CSS transitions only (transform/opacity); `?calm` turns them off. Never underlines.
import "./themes.css";
import "./chrome.css";
import wordmarkSvg from "./brand/the-dome-wordmark.svg" with { type: "text" };

// Official wordmark (traced from thedome.id), recoloured through currentColor; decorative, the link carries the name.
const wordmark = (cls: string) => wordmarkSvg.replace("<svg ", `<svg class="${cls}" aria-hidden="true" focusable="false" `).replace(/ role="img" aria-label="The Dome"/, "").replace(/<title>[^<]*<\/title>/, "");

export type ChromeActive = "home" | "book" | "events" | "visit";

const ADDRESS = "Jl. Arteri Permata Hijau No.98, Grogol Utara, Kebayoran Lama, Jakarta Selatan 12210";
const EMAIL = "info@thedome.id";
const WHATSAPP = { label: "WhatsApp +62 821 2128 4459", href: "https://wa.me/6282121284459" };
const APP = { label: "Get The Dome App", href: "https://thedome.id/download" };

const MENU: { label: string; href: string; key?: ChromeActive }[] = [
  { label: "Home", href: "/site", key: "home" },
  { label: "Book a court", href: "/site/book", key: "book" },
  { label: "Events", href: "/site/events", key: "events" },
  { label: "The club", href: "/site#club" },
  { label: "Visit", href: "/site#visit", key: "visit" },
];

const TABS: { label: string; href: string; key: ChromeActive; icon: string }[] = [
  { label: "Home", href: "/site", key: "home", icon: "M4 10.5 12 4l8 6.5V20h-5v-5.5H9V20H4z" },
  { label: "Book", href: "/site/book", key: "book", icon: "M6 5.5h12a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7.5a2 2 0 0 1 2-2zM4 10h16M8.5 3.5v4M15.5 3.5v4" },
  { label: "Events", href: "/site/events", key: "events", icon: "M20 12a8 8 0 1 1-16 0a8 8 0 1 1 16 0zM5.5 7.5c3 1.5 3 7.5 0 9M18.5 7.5c-3 1.5-3 7.5 0 9" },
  { label: "Visit", href: "/site#visit", key: "visit", icon: "M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11zM14.3 10a2.3 2.3 0 1 1-4.6 0a2.3 2.3 0 1 1 4.6 0z" },
];

const calm = () => new URLSearchParams(location.search).has("calm");

export function mountChrome(opts: { active: ChromeActive }): void {
  const { active } = opts;
  if (calm()) document.documentElement.classList.add("chrome-calm");
  const h = document.getElementById("chrome-header");
  const f = document.getElementById("chrome-footer");

  if (h) {
    h.classList.add("chrome-slot");
    h.innerHTML = `
      <a class="ch-skip btn btn-secondary" href="#main">Skip to content</a>
      <header class="ch-hdr">
        <div class="ch-hdr-in">
          <button class="btn btn-text ch-menu" type="button" aria-expanded="false" aria-controls="ch-overlay">Menu</button>
          <a class="ch-mark" href="/site" aria-label="The Dome, home">${wordmark("ch-logo")}</a>
          <a class="btn btn-secondary ch-book" href="/site/book"${active === "book" ? ' aria-current="page"' : ""}>Book</a>
        </div>
      </header>
      <div class="ch-overlay" id="ch-overlay" role="dialog" aria-modal="true" aria-label="Menu" hidden>
        <div class="ch-ov-top">
          <button class="btn btn-text ch-close" type="button">Close</button>
          <span class="ch-ov-mark" aria-hidden="true">${wordmark("ch-logo")}</span>
          <a class="btn btn-secondary ch-book" href="/site/book">Book</a>
        </div>
        <div class="ch-ov-body">
          <nav class="ch-ov-nav" aria-label="Site">
            <ul>
              ${MENU.map((m, i) => `<li style="--i:${i}"><a class="ch-ov-link" href="${m.href}"${m.key === active ? ' aria-current="page"' : ""}>${m.label}</a></li>`).join("")}
            </ul>
          </nav>
          <svg class="ch-ov-logo" viewBox="0 0 200 200" aria-hidden="true" focusable="false">
            <circle class="ch-ov-ring" cx="100" cy="100" r="88" pathLength="1" />
            <circle class="ch-ov-dot" cx="100" cy="12" r="5" />
          </svg>
        </div>
        <div class="ch-ov-foot">
          <p class="body ch-ov-addr">${ADDRESS}</p>
          <p class="body ch-ov-mail">${EMAIL}</p>
          <div class="ch-ov-actions">
            <a class="btn btn-secondary" href="${WHATSAPP.href}" target="_blank" rel="noopener">${WHATSAPP.label}</a>
            <a class="btn btn-secondary" href="${APP.href}" target="_blank" rel="noopener">${APP.label}</a>
          </div>
        </div>
      </div>
      <nav class="ch-tabbar" aria-label="Primary">
        ${TABS.map((t) => `<a class="ch-tab" href="${t.href}"${t.key === active ? ' aria-current="page"' : ""}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="${t.icon}"/></svg><span>${t.label}</span></a>`).join("")}
      </nav>`;
    wireMenu(h);
  }

  if (f) {
    f.innerHTML = `
      <footer class="ch-ftr" data-ground="deep">
        <div class="ch-ftr-in">
          <div class="ch-ftr-top">
            <div class="ch-ftr-brand">
              <p class="ch-ftr-mark" role="img" aria-label="The Dome">${wordmark("ch-ftr-logo")}</p>
              <p class="lead ch-ftr-invite">Consider this your invitation for a day well spent.</p>
              <a class="btn btn-primary ch-ftr-cta" href="/site/book">Book a court</a>
            </div>
            <div class="ch-ftr-cols">
              <div class="ch-ftr-col">
                <p class="ch-ftr-h">Visit</p>
                <p class="body ch-ftr-p">${ADDRESS}</p>
                <p class="body ch-ftr-p">Open 06:00 to 22:00, <span class="ch-nowrap">every day.</span></p>
              </div>
              <div class="ch-ftr-col">
                <p class="ch-ftr-h">Contact</p>
                <a class="ch-ftr-link" href="${WHATSAPP.href}" target="_blank" rel="noopener" aria-label="${WHATSAPP.label}">WhatsApp&nbsp;<span class="ch-nowrap">+62 821 2128 4459</span></a>
                <p class="body ch-ftr-p">${EMAIL}</p>
                <a class="ch-ftr-link" href="${APP.href}" target="_blank" rel="noopener">${APP.label}</a>
              </div>
              <div class="ch-ftr-col">
                <p class="ch-ftr-h">Explore</p>
                <a class="ch-ftr-link" href="/site/book">Book a court</a>
                <a class="ch-ftr-link" href="/site/events">Events</a>
                <a class="ch-ftr-link" href="/site/events#clinic">Bressel Academy</a>
                <a class="ch-ftr-link" href="/site#club">The club</a>
              </div>
            </div>
          </div>
          <div class="ch-ftr-bottom">
            <ul class="ch-ftr-legal">
              <li><a class="ch-ftr-link" href="#">Privacy Policy</a></li>
              <li><a class="ch-ftr-link" href="#">Terms of Service</a></li>
              <li><a class="ch-ftr-link" href="#">Account Deletion</a></li>
              <li><a class="ch-ftr-link" href="#">Refund Policy</a></li>
            </ul>
            <p class="caption ch-ftr-copy">Copyright 2026 The Dome. PT Bugaria Beruntung Bersama.</p>
          </div>
        </div>
      </footer>`;
  }
}

function wireMenu(root: HTMLElement) {
  const btn = root.querySelector<HTMLButtonElement>(".ch-menu")!;
  const ov = root.querySelector<HTMLElement>(".ch-overlay")!;
  const close = ov.querySelector<HTMLButtonElement>(".ch-close")!;
  let lastFocus: HTMLElement | null = null;

  const focusables = () => Array.from(ov.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
  const open = () => {
    lastFocus = document.activeElement as HTMLElement | null;
    ov.hidden = false;
    btn.setAttribute("aria-expanded", "true");
    document.documentElement.classList.add("ch-locked");
    window.dispatchEvent(new CustomEvent("chrome:menu", { detail: { open: true } }));
    requestAnimationFrame(() => {
      ov.setAttribute("data-open", "");
      close.focus();
    });
  };
  const shut = () => {
    ov.removeAttribute("data-open");
    btn.setAttribute("aria-expanded", "false");
    document.documentElement.classList.remove("ch-locked");
    window.dispatchEvent(new CustomEvent("chrome:menu", { detail: { open: false } }));
    const done = () => { ov.hidden = true; };
    if (document.documentElement.classList.contains("chrome-calm")) done();
    else setTimeout(done, 300);
    lastFocus?.focus();
  };
  btn.addEventListener("click", open);
  close.addEventListener("click", shut);
  ov.querySelectorAll("a").forEach((a) => a.addEventListener("click", () => shut()));
  ov.addEventListener("keydown", (e) => {
    if (e.key === "Escape") { e.preventDefault(); shut(); return; }
    if (e.key !== "Tab") return;
    const f = focusables();
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
}
